/**
 * Accuplacer "Student Information" — pixel-mirror of the official ACCUPLACER
 * registration screen (purple wordmark, black sub-header, lavender form panel,
 * red asterisks, black Save button).
 *
 * No login required — anyone can submit. On success: thank-you screen.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ChevronDown } from "lucide-react";

const COUNTRIES = ["Canada", "United States", "Other"];
const PROVINCES_CA = [
    "Alberta", "British Columbia", "Manitoba", "New Brunswick",
    "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
    "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
    "Yukon", "Other",
];
const STATES_US = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia",
    "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
    "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota",
    "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
    "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
    "Washington", "West Virginia", "Wisconsin", "Wyoming", "Other",
];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];

function fmtError(err, fallback = "Submission failed.") {
    const d = err?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "Invalid input").join("; ");
    return d.msg || JSON.stringify(d);
}

// ----------------------------------------------------------------------------

const labelCls = "block text-[11px] font-semibold tracking-wide text-[#3F2D6B] uppercase mb-1";
const reqStar = <span className="text-[#E03B3B] ml-0.5">*</span>;

function TextField({ label, required, value, onChange, ...rest }) {
    return (
        <div>
            <label className={labelCls}>
                {label} {required ? reqStar : null}
            </label>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="w-full h-9 px-2.5 border border-[#C7BBD9] bg-white text-sm rounded-sm focus:outline-none focus:border-[#5B3F94] focus:ring-1 focus:ring-[#5B3F94]/40"
                {...rest}
            />
        </div>
    );
}

function SelectField({ label, required, value, onChange, options, placeholder, ...rest }) {
    return (
        <div>
            <label className={labelCls}>
                {label} {required ? reqStar : null}
            </label>
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    required={required}
                    className="w-full h-9 pl-2.5 pr-8 border border-[#C7BBD9] bg-white text-sm rounded-sm appearance-none focus:outline-none focus:border-[#5B3F94] focus:ring-1 focus:ring-[#5B3F94]/40"
                    {...rest}
                >
                    {placeholder ? <option value="">{placeholder}</option> : null}
                    {options.map((o) => (
                        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
                            {typeof o === "string" ? o : o.label}
                        </option>
                    ))}
                </select>
                <ChevronDown className="h-3.5 w-3.5 absolute right-2 top-2.5 pointer-events-none text-[#5B3F94]" />
            </div>
        </div>
    );
}

function PhoneField({ label, required, value, onChange }) {
    return (
        <div>
            <label className={labelCls}>
                {label} {required ? reqStar : null}
            </label>
            <div className="flex items-center border border-[#C7BBD9] bg-white rounded-sm h-9 overflow-hidden">
                <span className="px-2 text-sm border-r border-[#C7BBD9] bg-[#F4EFFB] text-[#5B3F94]" title="Canada/US">
                    🇨🇦 +1
                </span>
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="555-123-4567"
                    className="flex-1 h-full px-2.5 text-sm focus:outline-none"
                />
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------------

export default function AccuplacerRegister() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        first_name: "", middle_name: "", last_name: "",
        address_line: "", address_line_2: "",
        country: "Canada", province: "", province_other: "",
        city: "", postal_code: "", email: "",
        student_id: "", confirm_student_id: "", supplemental_student_id: "",
        home_phone: "", mobile_phone: "",
        date_of_birth: "",
        gender: "",
        enrolled_in_high_school: "",
        share_institutions: [],
        opt_out_marketing: false,
        consent: true,
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [shareInput, setShareInput] = useState("");

    const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const provinceOptions = form.country === "United States" ? STATES_US : PROVINCES_CA;

    const addInstitution = () => {
        const trimmed = shareInput.trim();
        if (!trimmed) return;
        if (form.share_institutions.length >= 3) {
            toast.error("You can select up to three institutions.");
            return;
        }
        if (form.share_institutions.includes(trimmed)) return;
        upd("share_institutions", [...form.share_institutions, trimmed]);
        setShareInput("");
    };
    const removeInstitution = (name) =>
        upd("share_institutions", form.share_institutions.filter((n) => n !== name));

    const submit = async (e) => {
        e?.preventDefault();
        if (form.student_id.trim() !== form.confirm_student_id.trim()) {
            toast.error("Student ID and Confirm Student ID must match.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/accuplacer/profiles", form);
            setDone(true);
        } catch (err) {
            toast.error(fmtError(err));
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-[80vh] bg-white flex items-center justify-center px-6 py-16" data-testid="accuplacer-register-done">
                <div className="max-w-xl w-full text-center bg-[#F4EFFB] border border-[#C7BBD9] rounded p-10">
                    <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center mb-4">
                        <CheckCircle2 className="h-7 w-7" />
                    </div>
                    <h2 className="font-display text-2xl text-[#3F2D6B] mb-2">
                        Registration received
                    </h2>
                    <p className="text-sm text-[#5C6B64] mb-6">
                        Thank you, {form.first_name}. The Office of the Registrar will
                        review your profile and contact you at{" "}
                        <strong>{form.email}</strong> with your test details and
                        credentials. Please allow up to two business days.
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        className="px-5 py-2 bg-black text-white text-sm rounded-sm hover:bg-[#222]"
                        data-testid="accuplacer-back-home"
                    >
                        Back to home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white" data-testid="accuplacer-register-form">
            {/* Sub-header band */}
            <div className="bg-[#2A2A2A] border-t border-[#3A3A3A]">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <h1 className="text-white text-lg font-medium tracking-wide">Student Information</h1>
                    <button type="button" aria-label="Accessibility options" className="text-white/60 hover:text-white">
                        <span className="text-xl">♿</span>
                    </button>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={submit} className="max-w-6xl mx-auto px-6 py-6">
                {/* Instructions block */}
                <div className="text-sm text-[#1A1A1A] mb-4 space-y-2">
                    <p>
                        <strong>Instructions:</strong> Please enter your information exactly as it appears on
                        your school record. Refer to the{" "}
                        <a href="#" className="text-[#5B3F94] underline">Student Instructions</a>.
                    </p>
                    <p className="text-[13px]">
                        <span className="text-[#E03B3B]">*</span> Indicates required fields
                    </p>
                </div>

                {/* Meta block */}
                <div className="bg-[#F4EFFB] border border-[#C7BBD9] rounded-sm grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5 px-5 py-4 mb-5 text-[13px]">
                    <div>
                        <span className="text-[#5C5564]">Institution Name: </span>
                        <strong>Heritage Community College of Business Inc.</strong>
                    </div>
                    <div>
                        <span className="text-[#5C5564]">Branching Profile Name: </span>
                        <span className="text-[#E03B3B]">*</span>{" "}
                        <strong>HCC Standard Admission Test</strong>
                    </div>
                    <div>
                        <span className="text-[#5C5564]">Site Name: </span>
                        <strong>HCC | Surrey</strong>
                    </div>
                    <div>
                        <span className="text-[#5C5564]">Voucher Code: </span>
                        <strong>N/A</strong>
                    </div>
                </div>

                {/* Lavender form section */}
                <div className="bg-[#F4EFFB] border border-[#C7BBD9] rounded-sm p-5">
                    {/* Row 1 — names */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <TextField label="First Name" required value={form.first_name}
                            onChange={(v) => upd("first_name", v)} data-testid="reg-first-name" />
                        <TextField label="Middle Name" value={form.middle_name}
                            onChange={(v) => upd("middle_name", v)} data-testid="reg-middle-name" />
                        <TextField label="Last Name" required value={form.last_name}
                            onChange={(v) => upd("last_name", v)} data-testid="reg-last-name" />
                    </div>

                    {/* Row 2 — address lines */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <TextField label="Address 1" required value={form.address_line}
                            onChange={(v) => upd("address_line", v)} data-testid="reg-address-1" />
                        <TextField label="Address 2" value={form.address_line_2}
                            onChange={(v) => upd("address_line_2", v)} data-testid="reg-address-2" />
                    </div>

                    {/* Row 3 — country / province / other */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <SelectField label="Country" required value={form.country}
                            onChange={(v) => { upd("country", v); upd("province", ""); }}
                            options={COUNTRIES} placeholder="-- Select Country --" data-testid="reg-country" />
                        <SelectField label="Province" required value={form.province}
                            onChange={(v) => upd("province", v)}
                            options={provinceOptions} placeholder="-- Select Province --" data-testid="reg-province" />
                        <TextField label="If Other Specify" value={form.province_other}
                            onChange={(v) => upd("province_other", v)} data-testid="reg-province-other" />
                    </div>

                    {/* Row 4 — city / postal / email */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <TextField label="City" required value={form.city}
                            onChange={(v) => upd("city", v)} data-testid="reg-city" />
                        <TextField label="ZIP/Postal Code" required value={form.postal_code}
                            onChange={(v) => upd("postal_code", v)} data-testid="reg-postal" />
                        <TextField label="Email" required type="email" value={form.email}
                            onChange={(v) => upd("email", v)} data-testid="reg-email" />
                    </div>

                    {/* Row 5 — IDs */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <TextField label="Student ID" required value={form.student_id}
                            onChange={(v) => upd("student_id", v)} data-testid="reg-student-id" />
                        <TextField label="Confirm Student ID" required value={form.confirm_student_id}
                            onChange={(v) => upd("confirm_student_id", v)} data-testid="reg-confirm-student-id" />
                        <TextField label="Supplemental Student ID" value={form.supplemental_student_id}
                            onChange={(v) => upd("supplemental_student_id", v)} data-testid="reg-supp-student-id" />
                    </div>

                    {/* Row 6 — phones + DOB */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <PhoneField label="Home Phone Number" value={form.home_phone}
                            onChange={(v) => upd("home_phone", v)} data-testid="reg-home-phone" />
                        <PhoneField label="Mobile Phone Number" value={form.mobile_phone}
                            onChange={(v) => upd("mobile_phone", v)} data-testid="reg-mobile-phone" />
                        <TextField label="Date of Birth" required type="date" value={form.date_of_birth}
                            onChange={(v) => upd("date_of_birth", v)} data-testid="reg-dob" />
                    </div>

                    {/* Row 7 — gender (col 1 only) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <SelectField label="Gender" required value={form.gender}
                            onChange={(v) => upd("gender", v)}
                            options={GENDERS} placeholder="-- Select Gender --" data-testid="reg-gender" />
                        <div />
                        <div />
                    </div>

                    {/* Row 8 — highschool */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <SelectField label="Are you enrolled in high school?" required value={form.enrolled_in_high_school}
                            onChange={(v) => upd("enrolled_in_high_school", v)}
                            options={["Yes", "No"]} placeholder="-- Select --" data-testid="reg-hs" />
                    </div>

                    {/* Share scores section */}
                    <div className="mt-6 pt-4 border-t border-[#C7BBD9] text-[13px] text-[#1A1A1A] space-y-2">
                        <p>
                            Select up to three institutions to share your test results. Your home institution
                            will automatically receive your test results. It will not appear as an option.
                        </p>
                        <p>
                            Sharing your test scores is optional. If unsure now, you can send your scores
                            later through the Student Portal.
                        </p>
                    </div>
                    <div className="mt-3">
                        <label className={labelCls}>Select the institutions</label>
                        <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-[#C7BBD9] bg-white rounded-sm min-h-[36px]">
                            {form.share_institutions.map((name) => (
                                <span key={name} className="inline-flex items-center gap-1 bg-[#5B3F94] text-white text-xs px-2 py-0.5 rounded-sm">
                                    {name}
                                    <button type="button" onClick={() => removeInstitution(name)} className="hover:text-white/70">×</button>
                                </span>
                            ))}
                            <input
                                value={shareInput}
                                onChange={(e) => setShareInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                        e.preventDefault();
                                        addInstitution();
                                    }
                                }}
                                placeholder={form.share_institutions.length >= 3 ? "" : "Type an institution name and press Enter"}
                                disabled={form.share_institutions.length >= 3}
                                className="flex-1 min-w-[180px] text-sm h-7 focus:outline-none"
                                data-testid="reg-share-input"
                            />
                        </div>
                    </div>

                    {/* Marketing opt-out */}
                    <div className="mt-4">
                        <label className="inline-flex items-start gap-2 cursor-pointer text-[13px]">
                            <input
                                type="checkbox"
                                checked={form.opt_out_marketing}
                                onChange={(e) => upd("opt_out_marketing", e.target.checked)}
                                className="mt-0.5 accent-[#5B3F94]"
                                data-testid="reg-opt-out"
                            />
                            <span>
                                I prefer <strong>NOT</strong> to receive email from the College Board regarding
                                their free college planning services.
                            </span>
                        </label>
                    </div>

                    <div className="mt-3">
                        <a href="#" className="text-[#5B3F94] text-[13px] underline">
                            College Board Privacy Policy
                        </a>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-5">
                    <button
                        type="button"
                        onClick={() => navigate("/accuplacer")}
                        className="px-5 py-2 bg-white border border-[#C7BBD9] text-[#3F2D6B] text-sm rounded-sm hover:bg-[#F4EFFB]"
                        data-testid="reg-back"
                        disabled={busy}
                    >
                        Back
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        className="px-7 py-2 bg-black text-white text-sm rounded-sm hover:bg-[#222] inline-flex items-center gap-2"
                        data-testid="reg-submit"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Save
                    </button>
                </div>
            </form>
        </div>
    );
}
